import { DocumentStates, DocumentTypes, ManualAlignZoneCustomData, PDFTronTools } from 'types';
import { PDFManagerFactory } from 'pdftron';
import { actions } from 'pdftron/docManager/reducer';
import store from 'store/store';
import { Core } from '@pdftron/webviewer';
import WebViewerWrapper from 'pdftron/core/WebViewerWrapper';
import {
  app,
  files,
  inspection,
  getSourceOverprint,
  getTargetOverprint,
  getTargetFile,
  getSourceFile,
  getSourceLayers,
  getTargetLayers,
  getSourceSeparations,
  getTargetSeparations,
} from 'store';
import Layers from 'pdftron/core/Layers';
import { reportWebViewerError } from 'pdftron/utils/webViewerError';
import { watchRendering } from 'pdftron/utils/renderUtils';
import { buildLayers, convertWebviewerLayerToVerifyLayer } from 'utils/layerUtils';
import { buildSeparations, SYNTHETIC_ALL_SEPARATION } from 'utils/separationUtils';
import { isInspectionUrl } from 'utils/location';
import { AnnotationAction } from 'components/PDFViewer/Utils';
import { isEqual } from 'lodash';

class EventHandlers {
  private static getViewer(documentType: DocumentTypes): WebViewerWrapper | null {
    const pdfDocManager = PDFManagerFactory.getPDFDocManager();
    if (pdfDocManager) {
      return pdfDocManager.getInstance(documentType);
    }
    return null;
  }

  private static onDocumentLoaded(documentType: DocumentTypes) {
    const viewer = this.getViewer(documentType);
    if (!viewer) return;

    viewer.instance.Core.documentViewer.addEventListener('documentLoaded', async () => {
      const docViewer = viewer.instance.Core.documentViewer;
      const doc = docViewer.getDocument();

      // Reset color point counter when source document loads (once per inspection)
      if (documentType === DocumentTypes.source) {
        store.dispatch(inspection.actions.setColorPointId(1));
      }

      const overprintEnabled = (documentType === DocumentTypes.source ? getSourceOverprint : getTargetOverprint)(
        store.getState(),
      );
      const OverprintPreviewMode = viewer.instance.Core.OverprintPreviewMode;
      doc.setOverprintPreviewMode(overprintEnabled ? OverprintPreviewMode.ON : OverprintPreviewMode.OFF);

      // Layers/separations come from the CTF preprocessor (persisted in fileMetadata). When that
      // data is absent (older files) we fall back to PDFTron discovery. Saved session state
      // (pre-seeded into Redux before files load, e.g. a reopened inspection) takes precedence
      // over both so the user's previous toggles are preserved.
      const fileSelector = documentType === DocumentTypes.source ? getSourceFile : getTargetFile;
      const fileMetadata = fileSelector(store.getState())?.fileMetadata;
      const sessionLayers = (documentType === DocumentTypes.source ? getSourceLayers : getTargetLayers)(
        store.getState(),
      );
      const sessionSeparations = (documentType === DocumentTypes.source ? getSourceSeparations : getTargetSeparations)(
        store.getState(),
      );

      // Layers and separations must be coordinated: a separation plate only registers (and can
      // only be reliably hidden) once its ink has rendered, which requires the layer carrying that
      // ink to be visible while the plates load. So we discover separations first with layers at
      // their document-default visibility, then apply separation visibility, and only then hide
      // layers as needed. Hiding layers up front would suppress the ink and leave co-named
      // separations stuck enabled until the first manual toggle.
      let applyLayerVisibility: (() => Promise<void>) | null = null;
      let separationsLoaded = false;
      let layerVisibilityApplied = false;

      // Lets the visibility refresh below wait out the default render already underway on load.
      const rendering = watchRendering(docViewer);

      // Applies deferred layer visibility once separations have loaded, then one refresh for both.
      const tryApplyLayerVisibility = () => {
        if (!separationsLoaded || !applyLayerVisibility || layerVisibilityApplied) return;
        layerVisibilityApplied = true;
        const apply = applyLayerVisibility;
        applyLayerVisibility = null;
        const refresh = () => {
          if (docViewer.getDocument() !== doc) return;
          // Wait until rendering has stopped so a stale load render can't land after us and win.
          rendering.whenStopped(() => {
            if (docViewer.getDocument() !== doc) return;
            docViewer.refreshAll();
            docViewer.updateView();
          });
        };
        // Refresh on both settle paths so a failed layer apply still paints separations.
        Promise.resolve().then(apply).then(refresh, refresh);
      };

      // The single refresh is now owned by tryApplyLayerVisibility, which won't run until layer
      // discovery has produced something to apply. If discovery fails we still have separation
      // visibility to render, so substitute a no-op and let the refresh through. The no-op must not
      // displace a real applyLayerVisibility: the handler below can throw after assigning one, and
      // losing it would hide layers the user asked to hide.
      const onLayerDiscoveryFailed = () => {
        if (layerVisibilityApplied) return;
        applyLayerVisibility = applyLayerVisibility ?? (() => Promise.resolve());
        tryApplyLayerVisibility();
      };

      // Layers: prefer the preprocessor's allLayers; otherwise use the layers PDFTron discovered.
      // refreshLayerView matches by name against the document layers, so a flat preprocessor list
      // still toggles the right content. The panel (Redux) is updated immediately, but the viewer
      // refresh that actually hides content is deferred until separations have loaded.
      Promise.resolve(doc.getLayersArray())
        .then((docLayers) => {
          if (docViewer.getDocument() !== doc) return;
          const convertedDocLayers = docLayers.map((docLayer) => convertWebviewerLayerToVerifyLayer(docLayer));
          const layers = buildLayers(
            fileMetadata?.allLayers,
            convertedDocLayers,
            fileMetadata?.layersVisibility,
            sessionLayers,
          );
          // Only dispatch when the loaded layers actually differ from what's already in the store.
          // setLayers is an autosave-triggering action, so dispatching on an unchanged reload (e.g.
          // a reopened/completed inspection, or a file with no layers) would call updateInspection
          // and knock the inspection out of "completed", spuriously surfacing the outdated-inspection
          // banner. Nullish store state is normalized to [] so an empty load matches an empty store.
          if (!isEqual(layers, sessionLayers ?? [])) {
            store.dispatch(inspection.actions.setLayers({ documentType, layers }));
          }
          applyLayerVisibility = () => new Layers(viewer.instance).refreshLayerView(layers, { skipRefresh: true });
          tryApplyLayerVisibility();
        })
        // .catch, not the two-arg .then: a throw inside the handler above must reach the fallback too.
        .catch(onLayerDiscoveryFailed);

      // Separations: PDFTron has to rasterize the plates. enableColorSeparations() kicks off that
      // load asynchronously. We discover plates while layers are still visible (so co-named inks
      // register), build the panel once loading has finished, and only then hide layers. The
      // preprocessor's allSeparations gives the authoritative name list/order; rgb is sourced from
      // the plates PDFTron discovers (matched by name).
      doc.enableColorSeparations({ checkIfBaseColorsUsed: false });

      // Expected plate names from the preprocessor (the synthetic "All" plate is never shown).
      const expectedSeparationNames = (fileMetadata?.allSeparations ?? []).filter(
        (name) => name !== SYNTHETIC_ALL_SEPARATION,
      );
      const loadedSeparationNames = () =>
        (doc.getColorSeparations?.() ?? [])
          .map((separation) => separation.name)
          .filter((name) => name !== SYNTHETIC_ALL_SEPARATION);
      const allExpectedSeparationsLoaded = () =>
        expectedSeparationNames.length > 0 &&
        expectedSeparationNames.every((name) => loadedSeparationNames().includes(name));

      // Wait strategy: finalize as soon as every expected plate is present (the fast path for files
      // with preprocessor metadata). Otherwise fall back to a settle debounce that resets on each
      // newly discovered plate, with an absolute hard cap so we never hang (legacy/old reloads).
      const SEPARATION_SETTLE_MS = 800;
      const SEPARATION_HARD_CAP_MS = 5000;
      let settleTimer: ReturnType<typeof setTimeout> | undefined;
      let hardCapTimer: ReturnType<typeof setTimeout> | undefined;
      let separationsFinalized = false;

      const finalizeSeparations = (loadingComplete: boolean) => {
        if (separationsFinalized || docViewer.getDocument() !== doc) return;
        separationsFinalized = true;
        if (settleTimer) clearTimeout(settleTimer);
        if (hardCapTimer) clearTimeout(hardCapTimer);

        const discovered = (doc.getColorSeparations?.() ?? [])
          .filter((separation) => separation.name !== SYNTHETIC_ALL_SEPARATION)
          .map((separation) => ({ name: separation.name, rgb: separation.rgb }));
        const separations = buildSeparations(
          discovered,
          fileMetadata?.separationsToHide,
          fileMetadata?.allSeparations,
          sessionSeparations,
        );
        // Only dispatch when the loaded separations actually differ from what's already in the
        // store. setSeparations is an autosave-triggering action; dispatching on an unchanged
        // reload (or a file with no separations) would call updateInspection and knock the
        // inspection out of "completed", spuriously surfacing the outdated-inspection banner.
        // Nullish store state is normalized to [] so an empty load matches an empty store. The
        // viewer-side visibility below is still applied regardless so the rendered plates stay
        // correct.
        if (!isEqual(separations, sessionSeparations ?? [])) {
          store.dispatch(inspection.actions.setSeparations({ documentType, separations }));
        }

        // Plates are loaded (layers still visible), so disabling a plate here actually sticks.
        separations.forEach((s) => doc.enableSeparation(s.name, s.enabled));

        // Only drop back to true color when we know loading actually finished and nothing is
        // hidden. On the hard-cap fallback (loadingComplete = false) we keep separation mode on to
        // avoid turning the engine "cold" on a partial set and reintroducing the first-toggle bug.
        const hasHiddenSeparation = separations.some((s) => !s.enabled);
        if (loadingComplete && !hasHiddenSeparation) {
          doc.disableColorSeparations();
        }

        // Now that separation visibility is applied, hide the layers as needed. The refresh that
        // renders both this separation visibility and the layer visibility is owned by
        // tryApplyLayerVisibility.
        separationsLoaded = true;
        tryApplyLayerVisibility();
      };

      const scheduleSeparationSettle = () => {
        if (settleTimer) clearTimeout(settleTimer);
        // We only treat a settle as "complete" if at least one plate actually loaded; an empty
        // settle (legacy file whose plates never arrived) keeps the engine on rather than going cold.
        settleTimer = setTimeout(() => finalizeSeparations(loadedSeparationNames().length > 0), SEPARATION_SETTLE_MS);
      };

      const onColorSeparationAdded = (colorData: Core.Document.ColorSeparation) => {
        if (colorData.name === SYNTHETIC_ALL_SEPARATION) return;
        if (allExpectedSeparationsLoaded()) {
          finalizeSeparations(true);
        } else {
          scheduleSeparationSettle();
        }
      };

      docViewer.addEventListener('beforeDocumentUnloaded', () => {
        if (settleTimer) clearTimeout(settleTimer);
        if (hardCapTimer) clearTimeout(hardCapTimer);
        rendering.stopWatching();
        doc.removeEventListener('colorSeparationAdded', onColorSeparationAdded);
      });

      if (fileMetadata?.allSeparations && expectedSeparationNames.length === 0) {
        // Preprocessor says this file has no separations: finalize immediately so layers aren't
        // blocked waiting for plates that will never arrive.
        finalizeSeparations(true);
      } else {
        doc.addEventListener('colorSeparationAdded', onColorSeparationAdded);
        hardCapTimer = setTimeout(
          () => finalizeSeparations(loadedSeparationNames().length > 0),
          SEPARATION_HARD_CAP_MS,
        );

        // Plates may already have loaded between enableColorSeparations() and attaching the
        // listener; account for them so fast files don't wait on an event that already fired.
        if (allExpectedSeparationsLoaded()) {
          finalizeSeparations(true);
        } else if (loadedSeparationNames().length > 0) {
          scheduleSeparationSettle();
        }
      }

      store.dispatch(actions.loadDocument(documentType));
    });
  }

  private static onAnnotationsLoaded(documentType: DocumentTypes) {
    this.getViewer(documentType)?.instance.Core.documentViewer.addEventListener('annotationsLoaded', () => {
      const documentViewer = this.getViewer(documentType)?.instance.Core.documentViewer;
      if (documentType === DocumentTypes.source && documentViewer) {
        const annotations = documentViewer.getAnnotationManager().getAnnotationsList();
        const pdfDocManager = PDFManagerFactory.getPDFDocManager();
        const hasInternalAnnotations = annotations.some(
          (annotation: Core.Annotations.Annotation) => !pdfDocManager?.isGVAnnotation(annotation),
        );
        store.dispatch(actions.setHasInternalAnnotations(hasInternalAnnotations));
      }
      store.dispatch(actions.loadAnnotations(documentType));
    });
  }

  private static onAnnotationChanged(documentType: DocumentTypes) {
    this.getViewer(documentType)?.instance.Core.annotationManager.addEventListener(
      'annotationChanged',
      (
        annotations: Core.Annotations.Annotation[],
        action: string,
        info: Core.AnnotationManager.AnnotationChangedInfoObject,
      ) => {
        // PDFTron loads a bunch of annotations at the beginning that aren't ours.
        if (!info.imported) {
          if (!isInspectionUrl()) return;

          const pdfDocManager = PDFManagerFactory.getPDFDocManager();
          if (!pdfDocManager) return;

          const annotation = annotations && annotations[0];
          if (!annotation || !pdfDocManager.isGVAnnotation(annotation)) return;

          // Handle ManualAlignZone annotations (they don't have a ToolName set)
          // These annotations hold ImageBitmap resources that need cleanup on deletion
          const isManualAlignZone =
            annotation.getCustomData?.(ManualAlignZoneCustomData.manualAlignZoneAnnotation) === 'true';

          if (isManualAlignZone) {
            pdfDocManager.manualAlignZoneAnnotationChanged(documentType, annotation, action);
            return;
          }

          const handlers: Partial<
            Record<
              PDFTronTools,
              (documentType: DocumentTypes, annotation: Core.Annotations.Annotation, action: string) => void
            >
          > = {
            [PDFTronTools.MARQUEE_ZONE]: (...p) => pdfDocManager.marqueeAnnotationChanged(...p),
            [PDFTronTools.CROSSOUT]: (...p) => pdfDocManager.crossoutAnnotationChanged(...p),
            [PDFTronTools.ZONE]: (...p) => pdfDocManager.textZoneAnnotationChanged(...p),
            [PDFTronTools.GRAPHIC]: (...p) => pdfDocManager.graphicZoneAnnotationChanged(...p),
            [PDFTronTools.CROP]: (...p) => pdfDocManager.cropZoneAnnotationChanged(...p),
            [PDFTronTools.POLYGON]: (...p) => pdfDocManager.polygonZoneAnnotationChanged(...p),
            [PDFTronTools.RESYNC]: (...p) => pdfDocManager.resyncZoneAnnotationChanged(...p),
            [PDFTronTools.MARKUP]: (...p) => pdfDocManager.markupZoneAnnotationChanged(...p),
            [PDFTronTools.MERGE_GRAPHICS_ZONE]: (...p) => pdfDocManager.mergeGraphicsZoneAnnotationChanged(...p),
            [PDFTronTools.COLOR_PICKER]: (...p) => pdfDocManager.colorPointAnnotationChanged(...p),
          };

          const handler = handlers[annotation.ToolName as PDFTronTools];
          if (handler) {
            handler(documentType, annotation, action);
          }
        }
      },
    );
  }

  private static onAnnotationSelected(documentType: DocumentTypes) {
    this.getViewer(documentType)?.instance.Core.annotationManager.addEventListener(
      'annotationSelected',
      (annotations: Core.Annotations.Annotation[], action: any) => {
        if (action === AnnotationAction.SELECTED) {
          if (!isInspectionUrl()) return;

          const pdfDocManager = PDFManagerFactory.getPDFDocManager();
          if (!pdfDocManager) return;

          const annotation = annotations && annotations[0];
          if (!annotation) return;

          const isManualAlignZone =
            annotation.getCustomData?.(ManualAlignZoneCustomData.manualAlignZoneAnnotation) === 'true';
          if (isManualAlignZone) {
            pdfDocManager.manualAlignOverlayAnnotationSelected(documentType, annotation);
            return;
          }

          if (!pdfDocManager.isGVAnnotation(annotation)) return;

          const handlers: Partial<
            Record<
              PDFTronTools,
              (documentType: DocumentTypes, annotation: Core.Annotations.Annotation, action: string) => void
            >
          > = {
            [PDFTronTools.MARQUEE_ZONE]: (...p) => pdfDocManager.marqueeAnnotationSelected(...p),
            [PDFTronTools.CROSSOUT]: (...p) => pdfDocManager.crossoutAnnotationSelected(...p),
            [PDFTronTools.ZONE]: (...p) => pdfDocManager.textZoneAnnotationSelected(...p),
            [PDFTronTools.GRAPHIC]: (...p) => pdfDocManager.graphicZoneAnnotationSelected(...p),
            [PDFTronTools.CROP]: (...p) => pdfDocManager.cropZoneAnnotationSelected(...p),
            [PDFTronTools.POLYGON]: (...p) => pdfDocManager.polygonZoneAnnotationSelected(...p),
            [PDFTronTools.MARKUP]: (...p) => pdfDocManager.markupZoneAnnotationSelected(...p),
            [PDFTronTools.MERGE_GRAPHICS_ZONE]: (...p) => pdfDocManager.mergeGraphicsZoneAnnotationSelected(...p),
            [PDFTronTools.COLOR_PICKER]: (...p) => pdfDocManager.colorPointAnnotationSelected(...p),
          };

          const handler = handlers[annotation.ToolName as PDFTronTools];
          if (handler) {
            handler(documentType, annotation, action);
          }
        }
      },
    );
  }

  private static onViewerClick(documentType: DocumentTypes) {
    const viewer = this.getViewer(documentType);
    if (!viewer) return;

    viewer.instance.Core.documentViewer.addEventListener('mouseLeftUp', (e: any) => {
      if (!isInspectionUrl()) return;

      const state = store.getState();
      const manualAlignMode = state.inspection.manualAlignMode;

      // Handle manual align mode clicks first
      if (manualAlignMode.isActive) {
        const pdfDocManager = PDFManagerFactory.getPDFDocManager();
        if (pdfDocManager && 'handleManualAlignClick' in pdfDocManager) {
          (pdfDocManager as any).handleManualAlignClick(documentType, e);
        }
        return;
      }
    });
  }

  private static onDocumentLoadFailed(documentType: DocumentTypes) {
    const viewer = this.getViewer(documentType);
    if (!viewer) return;
    // PDFTron doesn't have a type for the e parameter.
    viewer.instance.UI.addEventListener(viewer.instance.UI.Events.LOAD_ERROR, (e: CustomEvent<any>) => {
      if (e.detail?.serverResponse?.status === 404) {
        const state = store.getState();
        const fileSelector = documentType === DocumentTypes.source ? getSourceFile : getTargetFile;
        const fileState = fileSelector(state);
        if (fileState?.fileId) {
          store.dispatch(app.actions.addUnavailableFile(fileState.fileId));
        }
        store.dispatch(files.actions.setFileUnavailable({ documentType, unavailable: true }));
        store.dispatch(
          inspection.actions.setLoadingState({
            documentType,
            documentState: DocumentStates.LOADED,
          }),
        );
      } else {
        // Any other load failure (corrupt/unsupported file, 5xx, decrypt failure) would otherwise
        // be silent. Surface it, then mark LOADED so spinners gated on getDocumentsLoaded release.
        const status = e.detail?.serverResponse?.status;
        reportWebViewerError({
          kind: 'load',
          documentType,
          error:
            e.detail instanceof Error ? e.detail : new Error(`WebViewer load error${status ? ` (${status})` : ''}`),
        });
        store.dispatch(
          inspection.actions.setLoadingState({
            documentType,
            documentState: DocumentStates.LOADED,
          }),
        );
      }
    });
  }

  public static register(documentType: DocumentTypes) {
    this.onDocumentLoaded(documentType);
    this.onDocumentLoadFailed(documentType);
    this.onAnnotationsLoaded(documentType);
    this.onAnnotationChanged(documentType);
    this.onAnnotationSelected(documentType);
    this.onViewerClick(documentType);
  }

  public static unregister(documentType: DocumentTypes) {
    const instance = this.getViewer(documentType)?.instance;

    if (instance) {
      instance?.Core.documentViewer.dispose();
    }
  }
}

export default EventHandlers;
